/* eslint-disable camelcase */
import sdk from '@/sdk';
import * as store from '@/store';
import eventBus, {FLOW_JS_LOADED} from '@/eventBus';
import {isInKuShare} from '@/utils/urlUtils';
import {EditorModeType} from '@/rte';
import {flowObj} from './flowObj';
import {denormalizeEmbedInfo, denormalizeEmbedInlineInfo} from './utils';
import type {IEmbedInlineDataTransfer, IEmbedIframeInfo, IFrameInfo} from './type';
import {useStableCallback} from './hooks';

export const renderSDKCard = async (url: string, container: HTMLElement) => {
    try {
        const flow = flowObj.getFlowApi();

        if (!flowObj.isRecognizeSupported()) {
            return;
        }

        // 渲染链接识别卡片
        flow.linkRecognize?.renderCard?.({
            url: decodeURIComponent(url),
            container,
        });
    } catch (err) {
        console.error(err);
        throw err;
    }
};

const render2EmbedContainer = async (
    docGuid: string, container: HTMLElement, iframeInfo: IEmbedIframeInfo | IFrameInfo, originUrl: string
) => {
    try {
        // 等待 flow.js 加载完成（如果正在加载中）
        if (!flowObj.getFlowApi()) {
            await new Promise<void>((resolve) => {
                eventBus.once(FLOW_JS_LOADED, () => resolve());
            });
        }

        const flow = flowObj.getFlowApi();

        if (!flow) {
            throw Error('flowJS 资源加载失败');
        }

        // 兼容老数据渲染（IFrameInfo 类型）
        const info = 'userData' in iframeInfo ? iframeInfo : denormalizeEmbedInfo(iframeInfo);
        const ugateToken = await sdk.as.getTDAUgateToken(info.userData.appkey);
        if (!ugateToken) {
            throw Error('ugateToken 获取失败');
        }

        const url = new URL(originUrl);
        const isKuShare = !!isInKuShare();

        url.searchParams.append('token', ugateToken);
        url.searchParams.append('isKuShare', isKuShare.toString());
        flow.iframe.render({
            container,
            type: 'IFRAME',
            info,
            url: url.href,
            docId: docGuid,
        });
    } catch (err) {
        console.error(err);
    }
};

export function useRender2EmbedContainerWrapper(docGuid: string) {
    return useStableCallback(
        async (container: HTMLElement, iframeInfo: IEmbedIframeInfo | IFrameInfo, originUrl: string) => {
            return render2EmbedContainer(docGuid, container, iframeInfo, originUrl);
        }
    );
}

type DataTransferEntry = [string, IEmbedInlineDataTransfer];

interface TableRenderRes {
    code: string;
    id?: string;
}

const renderEmbedInline = async (
    mode: EditorModeType, editable: boolean, portal: HTMLDivElement, dataTransferEntry: DataTransferEntry
) => {
    try {
        // 等待 flow.js 加载完成（如果正在加载中）
        if (!flowObj.getFlowApi()) {
            await new Promise<void>((resolve) => {
                eventBus.once(FLOW_JS_LOADED, () => resolve());
            });
        }

        const flow = flowObj.getFlowApi();

        if (!flow) {
            throw Error('flowJS 资源加载失败');
        }

        const sceneRole = ['editor', 'meeting'].includes(mode) && editable ? 'edit' : 'read';
        const [source, {byCopy, recognize_id, recognize_type, type, data, info}] = dataTransferEntry;
        const params: any = {
            container: portal,
            url: source,
        };

        if (byCopy) {
            params.copyId = recognize_id;
        } else {
            params.id = recognize_id;
        }

        let res: TableRenderRes | null = null;
        if (type === 'BLOCK' || recognize_type === 'BLOCK') {
            params.type = recognize_type;
            params.sceneRole = sceneRole;
            params.kuShareToken = store.getData(store.KEYS.KU_SHARE_PARAMS)?.token;
            res = await flow?.block?.render(params);
        }

        if (type === 'BLOCK_WORK_CARD') {
            // 兼容老数据渲染
            const paramsInfo = data ? denormalizeEmbedInlineInfo(data) : info;
            params.type = type;
            params.info = paramsInfo;
            res = await flow?.linkRecognize?.renderBlockCard(params);
        }

        const {id} = res || {};
        if (id) {
            return {
                recognize_id: id,
            };
        }
    } catch (err) {
        console.error(err);
    }
};

export function useRenderEmbedInlineWrapper(
    mode: EditorModeType,
    editable: boolean
) {
    return useStableCallback(
        async (portal: HTMLDivElement, dataTransferEntry: DataTransferEntry) => {
            return renderEmbedInline(mode, editable, portal, dataTransferEntry);
        }
    );
}
